'use client';

import { useQuery } from '@tanstack/react-query';
import type { BrowserScreenshotUrlResponseDto } from '@terra/shared-contracts-artifact';
import { artifactClient } from '@terra/shared-rest-clients-artifact';

export const BROWSER_SCREENSHOT_URL_QUERY_KEY = 'query.browser-screenshot-url';

const SIGNED_URL_STALE_TIME_MS = 10 * 60 * 1000;

const fetchBrowserScreenshotUrl = async (
  artifactId: string,
): Promise<BrowserScreenshotUrlResponseDto | null> => {
  const response = await artifactClient.getArtifactScreenshotUrl({
    params: { artifactId },
  });

  if (response.status === 200) {
    return response.body;
  }

  if (response.status === 404) {
    return null;
  }

  throw new Error('Failed to load browser screenshot URL');
};

export const useBrowserScreenshotUrl = (artifactId: string | undefined) => {
  const queryResult = useQuery({
    queryKey: [BROWSER_SCREENSHOT_URL_QUERY_KEY, artifactId],
    queryFn: () =>
      artifactId
        ? fetchBrowserScreenshotUrl(artifactId)
        : Promise.resolve(null),
    enabled: !!artifactId,
    staleTime: SIGNED_URL_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  return {
    screenshotUrl: queryResult.data ?? null,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
  };
};
